import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Upload, X, Building2, AlertCircle } from "lucide-react";

interface CompanyLogoUploaderProps {
  value?: string;
  onChange: (url: string) => void;
  companyName?: string;
  error?: string;
}

export function CompanyLogoUploader({ value, onChange, companyName = "", error }: CompanyLogoUploaderProps) {
  const [preview, setPreview] = useState<string>(value || "");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);

  const getInitials = (name: string): string => {
    const words = name.trim().split(' ');
    if (words.length === 1) {
      return words[0].substring(0, 2).toUpperCase();
    }
    return (words[0][0] + words[words.length - 1][0]).toUpperCase();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      alert("La imagen no debe superar 5MB");
      return;
    }

    if (!file.type.startsWith("image/")) {
      alert("Solo se permiten imágenes");
      return;
    }

    setUploading(true);

    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      setPreview(result);
      onChange(result);
      setUploading(false);
    };
    reader.readAsDataURL(file);
  };

  const handleRemove = () => {
    setPreview("");
    onChange("");
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <div className="flex flex-col items-center space-y-4">
      <Label>Logo de la Empresa (Opcional)</Label>
      
      <div className="relative">
        {preview ? (
          <div className="relative group">
            <img 
              src={preview} 
              alt="Logo preview" 
              className="w-32 h-32 rounded-xl object-cover border-4 border-slate-200 shadow-lg bg-white p-2"
            />
            <button
              type="button"
              onClick={handleRemove}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-2 hover:bg-red-600 shadow-lg transition-all opacity-0 group-hover:opacity-100"
              title="Eliminar logo"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        ) : (
          <div className="w-32 h-32 rounded-xl bg-gradient-to-br from-blue-400 to-purple-400 flex items-center justify-center border-4 border-slate-200 shadow-lg">
            {companyName ? (
              <span className="text-4xl font-bold text-white">
                {getInitials(companyName)}
              </span>
            ) : (
              <Building2 className="h-16 w-16 text-white" />
            )}
          </div>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileChange}
        className="hidden"
      />

      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleClick}
          disabled={uploading}
        >
          <Upload className="h-4 w-4 mr-2" />
          {preview ? "Cambiar Logo" : "Subir Logo"}
        </Button>
        {preview && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleRemove}
            className="text-red-600 hover:text-red-700"
          >
            Eliminar
          </Button>
        )}
      </div>

      <p className="text-xs text-slate-500 text-center">
        Máximo 5MB • JPG, PNG, SVG
      </p>

      {error && (
        <p className="text-red-500 text-sm flex items-center">
          <AlertCircle className="h-3 w-3 mr-1" />
          {error}
        </p>
      )}
    </div>
  );
}